/**
 * Preview Effect Class Utilities
 *
 * Computes CSS class names for text and image content based on
 * active preview options. Handles class stacking for combined effects.
 *
 * @module previewEffectClasses
 */

import { contentBlurStyles } from '@soteria/design-system-ve';

import type { PreviewOptions } from '../domain/PreviewOptions';
import * as effects from '../styles/previewEffects.css';

/**
 * Get combined CSS class names for text content based on active preview options.
 *
 * Text content only responds to the blur effect.
 * Uses the DS blurredText style (4px) for lighter text blur.
 * Gray and Flip do not apply to text.
 */
export function getTextEffectClasses(options?: PreviewOptions): string | undefined {
  if (!options?.blur) return undefined;

  return contentBlurStyles.blurredText;
}

/**
 * Get combined CSS class names for image content based on active preview options.
 *
 * Images respond to all three effects: blur, gray, and flip.
 * When both blur and gray are active, a combined filter class is used
 * to avoid overriding separate filter declarations.
 */
export function getImageEffectClasses(options?: PreviewOptions): string | undefined {
  if (!options) return undefined;

  const classes: string[] = [];

  // Blur + Gray must use a combined filter class (CSS filter values don't stack across classes)
  if (options.blur && options.gray) {
    classes.push(effects.previewBlurGray);
  } else if (options.blur) {
    classes.push(effects.previewBlur);
  } else if (options.gray) {
    classes.push(effects.previewGray);
  }

  if (options.flip) {
    classes.push(effects.previewFlip);
  }

  return classes.length > 0 ? classes.join(' ') : undefined;
}
